import ServiceApi from '../services/service.api';

const baseServiceUrl = `/randox`;

type LabTest = {
  id: number;
  service_code: string;
};

type PostcodeSearch = {
  postcode: string;
};

export const LabTestProviders = {
  RANDOX: 'RANDOX',
  INUVI: 'INUVI'
} as const;

export type LabTestProviderCode = (typeof LabTestProviders)[keyof typeof LabTestProviders];

export type BaseOrderParams = {
  case_id: number;
  appt_id: number;
  pat_guid: string;
  lab_tests: LabTest[];
  order_route_code: string;
  appt_type?: string;
  selectedClinicSlot?: {
    id: string;
    name: string;
    postcode: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    distance: number;
    slotId: string;
    date: string;
    time: string;
  };
};

type AddressParams = {
  addline1: string;
  addline2?: string;
  addline3?: string;
  city?: string;
  county?: string;
  postcode: string;
};

export type RandoxInClinicParams = {
  appt_id: number;
  slot_id: string;
  slot_date: string;
  slot_time: string;
  location_id: string;
};

export type RandoxHomeTestKitParams = {
  appt_id: number;
  postcode: string;
  addline1: string;
  addline2?: string;
  city?: string;
  country?: string;
  order_route_code: string;
};

export type OrderParamsWithAddress = BaseOrderParams & AddressParams;

export type OrderParamsWithOrderId = OrderParamsWithAddress & {
  ext_order_id: string;
  lab_test_provider_code?: LabTestProviderCode;
};

export type InuviOrderParams = OrderParamsWithAddress & {
  policyNumber?: string;
};

export type OrderParams = BaseOrderParams | OrderParamsWithAddress | OrderParamsWithOrderId;

export type UnlinkFailedTestsParams = {
  orderNumber: string;
  confirmedFailedTestIds: number[];
  adminUserGuid: string;
};

export type UnlinkFailedTestsResponse = {
  success: boolean;
  originalOrder?: string;
  newOrder?: string;
  reorderedTestCount?: number;
  message: string;
  error?: string;
};

const ApiRandox = {
  getRandoxByID: (
    order_number: string,
    pat_user_guid: string,
    order_route_code: string,
    internal_kit_order_id?: string,
    hasTrackingInfo?: boolean,
    results_received_date?: string
  ) => {
    const params = {
      order_number,
      internal_kit_order_id,
      order_route_code,
      hasTrackingInfo,
      results_received_date,
      action: 'updateOrderStatus',
      pat_user_guid
    };
    return ServiceApi.post(`${baseServiceUrl}`, params);
  },
  assignOrderId: (
    order_number: string,
    lab_test_ids: number[],
    pat_user_guid: string,
    appt_id: number,
    order_route_code: string
  ) => {
    const params = {
      order_number,
      lab_test_ids,
      action: 'assignOrderId',
      pat_user_guid,
      appt_id,
      order_route_code
    };
    return ServiceApi.post(`${baseServiceUrl}`, params);
  },
  createStoolOrder: async (params: OrderParamsWithAddress) => {
    const body = {
      action: 'createStoolOrder',
      lab_test_provider_code: LabTestProviders.RANDOX,
      ...params
    };
    return ServiceApi.post(`${baseServiceUrl}`, body);
  },
  createHomeTestKitOrder: async (params: OrderParamsWithOrderId) => {
    const { lab_test_provider_code = LabTestProviders.RANDOX, ...rest } = params;
    const body = {
      action: 'createHomeTestKitOrder',
      lab_test_provider_code,
      ...rest
    };
    return ServiceApi.post(`${baseServiceUrl}`, body);
  },
  createBloodRandoxOrder: async (params: BaseOrderParams) => {
    const body = {
      action: 'createBloodRandoxOrder',
      lab_test_provider_code: LabTestProviders.RANDOX,
      ...params
    };
    return ServiceApi.post(`${baseServiceUrl}`, body);
  },
  createInuviOrder: async (params: InuviOrderParams) => {
    const body = {
      action: 'createBloodInuviOrder',
      lab_test_provider_code: LabTestProviders.INUVI,
      ...params
    };
    return ServiceApi.post(`${baseServiceUrl}`, body);
  },
  getClinicLocations: (params: PostcodeSearch) => {
    const body = {
      action: 'getClinicLocations',
      ...params
    };
    return ServiceApi.post(`${baseServiceUrl}`, body);
  },
  getRandoxClinicLocations: (params: PostcodeSearch) => {
    const body = {
      action: 'getRandoxClinicAvailability',
      ...params
    };
    return ServiceApi.post(`${baseServiceUrl}`, body);
  },
  createRandoxInClinicOrder: async (params: RandoxInClinicParams) => {
    const body = {
      action: 'createNexusAndBookRandoxClinic',
      lab_test_provider_code: LabTestProviders.RANDOX,
      ...params
    };
    return ServiceApi.post(`${baseServiceUrl}`, body);
  },
  createPatientHomeTestKitOrder: (params: RandoxHomeTestKitParams) => {
    const body = {
      action: 'createStoolOrder',
      lab_test_provider_code: LabTestProviders.RANDOX,
      ...params
    };
    return ServiceApi.post(`${baseServiceUrl}`, body);
  },
  unlinkFailedTests: (params: UnlinkFailedTestsParams): Promise<{ data: UnlinkFailedTestsResponse }> => {
    const body = {
      action: 'unlinkFailedTests',
      ...params
    };
    return ServiceApi.post(`${baseServiceUrl}`, body);
  },
  getAdobeSdkKey: (): Promise<{ data: { adobe_sdk_key: string } }> => {
    const body = { action: 'getAdobeSdkKey' };
    return ServiceApi.post(`${baseServiceUrl}`, body);
  },
  cancelLabOrder: async (params: {
    provider: 'RANDOX' | 'INUVI';
    order_number: string;
    current_status: string;
    randox_order_id?: number;
    home_dispatch_order_id?: number;
    inuvi_order_exam_id?: string;
    randox_booking_order_id?: number;
    order_route_code?: string;
    appt_id?: number;
  }) => {
    const body = {
      action: 'cancelLabOrder',
      ...params
    };
    return ServiceApi.post(`${baseServiceUrl}`, body);
  }
};

export default ApiRandox;
